/**
 * Pitch Deck page — slide viewer with editorial styling that matches the PPTX export.
 *
 * Layout:
 *  - Top bar: project name + actions (back, regenerate, export PPTX, export PDF)
 *  - Left rail: thumbnail list of slides (numbered, click to navigate)
 *  - Centre: live preview of the active slide (16:9 letterboxed)
 *  - Right panel: editable fields for the active slide (title/subtitle/bullets/metric)
 *
 * Keyboard: ←/→ navigate slides, Esc closes back to project.
 * Editing persists via PUT /api/ai/pitchdeck/{project_id}.
 * PDF export rasterises each slide via html2canvas and assembles a jsPDF.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Sparkles,
  Save,
  Plus,
  Trash2,
  RefreshCw,
  TrendingUp,
  Lock,
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CapTableSimulator } from "@/components/CapTableSimulator";
import {
  PrimaryButton,
  SecondaryButton,
  Overline,
  TextInput,
  TextArea,
} from "@/components/ui-kit";

// Slide layout constants. The slide is rendered at 1280x720 (16:9), scaled to fit.
const SLIDE_W = 1280;
const SLIDE_H = 720;

const SLIDE_LABELS_ES = {
  cover: "Portada",
  problem: "Problema",
  solution: "Solución",
  market: "Mercado",
  product: "Producto",
  business_model: "Modelo de negocio",
  traction: "Tracción",
  competition: "Competencia",
  team: "Equipo",
  financials: "Financieros",
  ask: "Petición",
  closing: "Cierre",
};
const SLIDE_LABELS_EN = {
  cover: "Cover",
  problem: "Problem",
  solution: "Solution",
  market: "Market",
  product: "Product",
  business_model: "Business model",
  traction: "Traction",
  competition: "Competition",
  team: "Team",
  financials: "Financials",
  ask: "Ask",
  closing: "Closing",
};

// ============================================================================
// Slide render — must visually match the PPTX as closely as possible
// ============================================================================

function CoverSlide({ slide, image, projectCover }) {
  const imageUrl = projectCover || image;
  return (
    <div className="absolute inset-0 bg-[#2563EB] overflow-hidden">
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
          crossOrigin="anonymous"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-[#2563EB]/50 via-[#2563EB]/85 to-[#2563EB]" />
      <div className="absolute left-[6%] right-[6%] top-1/2 -translate-y-1/2">
        <div className="w-16 h-1 bg-[#B89154] mb-8" />
        <h1
          className="text-[#F5EFE6] leading-[1.05] tracking-tight"
          style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: "84px" }}
        >
          {slide.title}
        </h1>
        {slide.subtitle && (
          <p
            className="mt-8 text-[#F5EFE6]/85 leading-relaxed max-w-3xl"
            style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "22px" }}
          >
            {slide.subtitle}
          </p>
        )}
      </div>
      <div
        className="absolute left-[6%] bottom-[6%] text-[#B89154] tracking-[0.3em] uppercase"
        style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "12px" }}
      >
        ATELIER · BUSINESS
      </div>
    </div>
  );
}

function ClosingSlide({ slide }) {
  return (
    <div className="absolute inset-0 bg-[#2563EB] overflow-hidden">
      <div className="absolute left-[6%] right-[6%] top-1/2 -translate-y-1/2">
        <div className="w-16 h-1 bg-[#B89154] mb-8" />
        <h1
          className="text-[#F5EFE6] leading-[1.05]"
          style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: "104px" }}
        >
          {slide.title || "Gracias"}
        </h1>
        {slide.subtitle && (
          <p
            className="mt-8 text-[#F5EFE6]/85"
            style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "22px" }}
          >
            {slide.subtitle}
          </p>
        )}
      </div>
      <div
        className="absolute left-[6%] bottom-[6%] text-[#B89154] tracking-[0.3em] uppercase"
        style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "12px" }}
      >
        ATELIER · BUSINESS
      </div>
    </div>
  );
}

function ContentSlide({ slide, image, idx, total, slideLabel }) {
  return (
    <div className="absolute inset-0 bg-[#F9F8F6] overflow-hidden flex">
      {/* Left content column */}
      <div className="flex-1 flex flex-col px-[6%] py-[6%] relative z-10 min-w-0">
        <div
          className="text-[#5C5B5A] tracking-[0.3em] uppercase mb-1"
          style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "13px" }}
        >
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} &nbsp;·&nbsp; {slideLabel}
        </div>
        <div className="w-12 h-1 bg-[#B89154] my-4" />
        <h2
          className="text-[#1A1A1A] leading-[1.1] tracking-tight"
          style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: "56px" }}
        >
          {slide.title}
        </h2>
        {slide.subtitle && (
          <p
            className="mt-3 text-[#5C5B5A]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 400, fontSize: "22px" }}
          >
            {slide.subtitle}
          </p>
        )}

        <div className="h-px bg-[#E8E6E1] my-7" />

        {/* Metric (if present) */}
        {slide.metric?.value && (
          <div className="mb-6">
            <div
              className="text-[#2563EB]"
              style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "68px", fontWeight: 700, lineHeight: 1 }}
            >
              {slide.metric.value}
            </div>
            {slide.metric.label && (
              <div
                className="text-[#5C5B5A] mt-2 tracking-wider uppercase"
                style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "14px" }}
              >
                {slide.metric.label}
              </div>
            )}
          </div>
        )}

        <ul className="space-y-3">
          {(slide.bullets || []).slice(0, 6).map((b, i) => (
            <li key={i} className="flex gap-3 items-baseline">
              <span
                className="text-[#B89154] shrink-0"
                style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "20px" }}
              >
                ·
              </span>
              <span
                className="text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 400, fontSize: "22px", lineHeight: 1.4 }}
              >
                {b}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-6">
          <div
            className="text-[#5C5B5A] tracking-[0.3em] uppercase"
            style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "10px" }}
          >
            ATELIER · BUSINESS
          </div>
        </div>
      </div>

      {/* Right image column */}
      <div className="w-[37%] relative shrink-0">
        {image && (
          <img
            src={image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            crossOrigin="anonymous"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#F9F8F6]/0" />
      </div>
    </div>
  );
}

function Slide({ slide, image, projectCover, idx, total, lang }) {
  const labels = lang === "es" ? SLIDE_LABELS_ES : SLIDE_LABELS_EN;
  const label = labels[slide.key] || slide.key;
  if (slide.key === "cover") return <CoverSlide slide={slide} image={image} projectCover={projectCover} />;
  if (slide.key === "closing") return <ClosingSlide slide={slide} />;
  return <ContentSlide slide={slide} image={image} idx={idx} total={total} slideLabel={label} />;
}

// ============================================================================
// Edit panel
// ============================================================================

function EditPanel({ slide, onUpdate, lang }) {
  function setField(k, v) {
    onUpdate({ ...slide, [k]: v });
  }
  function setBullet(i, v) {
    const next = [...(slide.bullets || [])];
    next[i] = v;
    setField("bullets", next);
  }
  function addBullet() {
    setField("bullets", [...(slide.bullets || []), ""]);
  }
  function removeBullet(i) {
    setField("bullets", (slide.bullets || []).filter((_, idx) => idx !== i));
  }
  function setMetric(k, v) {
    setField("metric", { ...(slide.metric || {}), [k]: v });
  }

  const showMetric = ["market", "business_model", "financials"].includes(slide.key);
  const showSubtitle = ["cover", "closing", "ask"].includes(slide.key) || !!slide.subtitle;

  return (
    <div className="bg-white border border-[#E8E6E1] rounded-sm p-6 space-y-5" data-testid="deck-edit-panel">
      <div>
        <Overline>{lang === "es" ? "Editar diapositiva" : "Edit slide"}</Overline>
        <p className="text-xs text-[#8A8886] mt-1">
          {lang === "es" ? "Los cambios se guardan al pulsar Guardar." : "Changes save on click."}
        </p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[#8A8886] mb-1.5 block">
          {lang === "es" ? "Título" : "Title"}
        </label>
        <TextInput value={slide.title || ""} onChange={(v) => setField("title", v)} testId="slide-title-input" />
      </div>

      {showSubtitle && (
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[#8A8886] mb-1.5 block">
            {lang === "es" ? "Subtítulo" : "Subtitle"}
          </label>
          <TextArea value={slide.subtitle || ""} onChange={(v) => setField("subtitle", v)} rows={2} testId="slide-subtitle-input" />
        </div>
      )}

      {showMetric && (
        <div className="grid grid-cols-[1fr_1fr] gap-3 p-4 border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#8A8886] mb-1.5 block">
              {lang === "es" ? "Métrica" : "Metric"}
            </label>
            <TextInput value={slide.metric?.value || ""} onChange={(v) => setMetric("value", v)} testId="slide-metric-value-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#8A8886] mb-1.5 block">
              {lang === "es" ? "Etiqueta" : "Label"}
            </label>
            <TextInput value={slide.metric?.label || ""} onChange={(v) => setMetric("label", v)} testId="slide-metric-label-input" />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs uppercase tracking-[0.18em] text-[#8A8886]">
            {lang === "es" ? "Bullets" : "Bullets"}
          </label>
          <button
            onClick={addBullet}
            className="text-xs text-[#2563EB] inline-flex items-center gap-1 hover:underline"
            data-testid="add-bullet-btn"
          >
            <Plus className="h-3 w-3" strokeWidth={2} /> {lang === "es" ? "Añadir" : "Add"}
          </button>
        </div>
        <div className="space-y-2">
          {(slide.bullets || []).map((b, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-[#8A8886] font-mono text-xs mt-2.5">{i + 1}</span>
              <TextArea value={b} onChange={(v) => setBullet(i, v)} rows={2} testId={`slide-bullet-${i}-input`} />
              <button onClick={() => removeBullet(i)} className="text-[#8A8886] hover:text-[#9E2A2B] mt-2.5" data-testid={`slide-bullet-${i}-remove`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function PitchDeck() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const { user, canUseAi } = useAuth();
  const { t, lang } = useLang();

  const [deck, setDeck] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectCover, setProjectCover] = useState("");
  const [slideImages, setSlideImages] = useState({});
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const slideRefs = useRef([]);

  // ---- Loaders ------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ai/pitchdeck/${projectId}`);
      const data = res.data;
      if (!data.pitchdeck) {
        // No deck yet — auto-build it from the plan modules.
        // Auto-build calls Claude, so it requires an active subscription.
        if (!canUseAi) {
          setDeck(null);
          setProjectName(data.project_name || "");
          setProjectCover(data.cover_image_url || "");
          return;
        }
        const built = await api.post(`/ai/pitchdeck/${projectId}/auto-build`);
        setDeck(built.data.pitchdeck.content);
        setSlideImages(built.data.slide_images || {});
        setProjectName(built.data.project_name || "");
        setProjectCover(built.data.cover_image_url || "");
      } else {
        setDeck(data.pitchdeck.content);
        setSlideImages(data.slide_images || {});
        setProjectName(data.project_name || "");
        setProjectCover(data.cover_image_url || "");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, canUseAi]);

  useEffect(() => { load(); }, [load]);

  // ---- Mutations ----------------------------------------------------------
  function updateActive(next) {
    setDeck((d) => ({ ...d, slides: d.slides.map((s, i) => (i === active ? next : s)) }));
  }
  async function save() {
    if (!deck) return;
    setSaving(true);
    try {
      await api.put(`/ai/pitchdeck/${projectId}`, { content: deck });
      toast.success(lang === "es" ? "Diapositivas guardadas" : "Slides saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }
  async function rebuildFromPlan(investorMode = false) {
    setBuilding(true);
    try {
      const res = await api.post(`/ai/pitchdeck/${projectId}/auto-build${investorMode ? "?investor_mode=true" : ""}`);
      setDeck(res.data.pitchdeck.content);
      setSlideImages(res.data.slide_images || {});
      setActive(0);
      toast.success(lang === "es"
        ? (investorMode ? "Pitch deck para inversores generado" : "Diapositivas reconstruidas desde el plan")
        : (investorMode ? "Investor pitch deck generated" : "Slides rebuilt from plan"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBuilding(false);
    }
  }

  // ---- Exports ------------------------------------------------------------
  async function downloadPPTX() {
    // Persist any unsaved edits first so the backend export matches preview.
    setDownloading("pptx");
    try {
      await api.put(`/ai/pitchdeck/${projectId}`, { content: deck });
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/pitchdeck/${projectId}/export.pptx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(deck.company_name || "deck").replace(/\s+/g, "_")}_pitchdeck.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PPTX " + (lang === "es" ? "descargado" : "downloaded"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  }

  async function downloadPDF() {
    if (!deck) return;
    setDownloading("pdf");
    try {
      // Render every slide off-screen so html2canvas can capture them at full
      // 1280x720 regardless of the user's current viewport. We mount a hidden
      // container, snap each ref, then assemble the PDF.
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: [SLIDE_W, SLIDE_H] });
      for (let i = 0; i < deck.slides.length; i++) {
        const node = slideRefs.current[i];
        if (!node) continue;
        const canvas = await html2canvas(node, {
          width: SLIDE_W,
          height: SLIDE_H,
          scale: 1.5,
          useCORS: true,
          backgroundColor: null,
          logging: false,
        });
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        if (i > 0) pdf.addPage([SLIDE_W, SLIDE_H], "landscape");
        pdf.addImage(dataUrl, "JPEG", 0, 0, SLIDE_W, SLIDE_H, undefined, "FAST");
      }
      pdf.save(`${(deck.company_name || "deck").replace(/\s+/g, "_")}_pitchdeck.pdf`);
      toast.success("PDF " + (lang === "es" ? "descargado" : "downloaded"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  }

  // ---- Keyboard nav -------------------------------------------------------
  useEffect(() => {
    function onKey(e) {
      if (!deck) return;
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") setActive((i) => Math.min(i + 1, deck.slides.length - 1));
      if (e.key === "ArrowLeft") setActive((i) => Math.max(0, i - 1));
      if (e.key === "Escape") nav(`/projects/${projectId}`);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deck, nav, projectId]);

  // ---- Render -------------------------------------------------------------
  const labels = lang === "es" ? SLIDE_LABELS_ES : SLIDE_LABELS_EN;

  const slideImageFor = useMemo(() => (slide) => slideImages[slide.key] || "", [slideImages]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#5C5B5A] text-sm tracking-[0.2em] uppercase">{t("common.loading")}</div>
      </div>
    );
  }
  if (!deck) {
    if (!canUseAi) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
          <div className="max-w-lg w-full bg-white border border-[#B89154]/40 rounded-sm p-10 text-center shadow-sm" data-testid="pitchdeck-ai-locked">
            <Lock className="h-10 w-10 text-[#B89154] mx-auto" strokeWidth={1.2} />
            <h3 className="mt-4 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              {t("paywall.ai_locked_title")}
            </h3>
            <p className="mt-3 text-sm text-[#5C5B5A]">
              {lang === "es"
                ? "El pitch deck se genera con IA. Activa Starter (49,90 €/mes) o Pro (99,90 €/mes) para crear, regenerar y exportar tu deck."
                : "Pitch deck generation uses AI. Activate Starter ($49.90/mo) or Pro ($99.90/mo) to create, regenerate and export your deck."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => nav("/pricing")}
                className="inline-flex items-center gap-2 bg-[#2563EB] text-[#F9F8F6] hover:bg-[#1D4ED8] rounded-sm px-5 py-2.5 text-sm transition-all"
                data-testid="pitchdeck-upgrade-btn"
              >
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {t("paywall.see_plans")}
              </button>
              <Link
                to={`/projects/${projectId}`}
                className="inline-flex items-center gap-2 border border-[#E8E6E1] hover:border-[#1A1A1A] rounded-sm px-5 py-2.5 text-sm transition-all"
                data-testid="pitchdeck-back-to-project-btn"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                {lang === "es" ? "Volver al proyecto" : "Back to project"}
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#5C5B5A] text-sm">{lang === "es" ? "Sin contenido." : "No content."}</div>
      </div>
    );
  }

  const current = deck.slides[active];

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col">
      {/* HEADER */}
      <header className="border-b border-[#E8E6E1] bg-white">
        <div className="px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to={`/projects/${projectId}`}
              className="inline-flex items-center gap-1 text-sm text-[#5C5B5A] hover:text-[#1A1A1A]"
              data-testid="back-to-project-link"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              {lang === "es" ? "Volver" : "Back"}
            </Link>
            <div className="h-6 w-px bg-[#E8E6E1]" />
            <div className="min-w-0">
              <Overline>{lang === "es" ? "Pitch deck" : "Pitch deck"}</Overline>
              <h1
                className="text-lg lg:text-xl tracking-tight text-[#1A1A1A] truncate"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                data-testid="deck-project-name"
              >
                {projectName || deck.company_name}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            {canUseAi ? (
              <>
                <SecondaryButton onClick={() => rebuildFromPlan(false)} disabled={building} testId="rebuild-deck-btn">
                  <RefreshCw className={`h-4 w-4 ${building ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  {lang === "es" ? "Reconstruir" : "Rebuild"}
                </SecondaryButton>
                <SecondaryButton onClick={() => rebuildFromPlan(true)} disabled={building} testId="rebuild-investor-btn">
                  <TrendingUp className={`h-4 w-4 ${building ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  {lang === "es" ? "Modo inversor" : "Investor mode"}
                </SecondaryButton>
              </>
            ) : (
              <Link to="/pricing">
                <SecondaryButton testId="pitchdeck-ai-locked-btn">
                  <Lock className="h-4 w-4" strokeWidth={1.5} />
                  {lang === "es" ? "IA bloqueada" : "AI locked"}
                </SecondaryButton>
              </Link>
            )}
            <SecondaryButton onClick={save} disabled={saving} testId="save-deck-btn">
              <Save className="h-4 w-4" strokeWidth={1.5} />
              {saving ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar" : "Save")}
            </SecondaryButton>
            <SecondaryButton onClick={downloadPDF} disabled={!!downloading} testId="export-pdf-btn">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
              {downloading === "pdf" ? "PDF…" : "PDF"}
            </SecondaryButton>
            <PrimaryButton onClick={downloadPPTX} disabled={!!downloading} testId="export-pptx-btn">
              <Download className="h-4 w-4" strokeWidth={1.5} />
              {downloading === "pptx" ? "PPTX…" : "PPTX"}
            </PrimaryButton>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[200px_1fr_360px] gap-0 min-h-0">
        {/* Slide list */}
        <aside className="border-r border-[#E8E6E1] bg-white overflow-y-auto" data-testid="slide-list">
          <div className="p-4 space-y-1.5">
            {deck.slides.map((s, i) => {
              const label = labels[s.key] || s.key;
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`w-full text-left px-3 py-2.5 rounded-sm flex items-center gap-3 transition-colors ${
                    isActive ? "bg-[#2563EB] text-[#F9F8F6]" : "hover:bg-[#F5F4F0] text-[#1A1A1A]"
                  }`}
                  data-testid={`slide-thumb-${i}`}
                >
                  <span
                    className={`font-mono text-[10px] shrink-0 ${isActive ? "text-[#B89154]" : "text-[#8A8886]"}`}
                    style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm truncate flex-1">{label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Preview */}
        <section className="p-6 lg:p-10 flex flex-col items-center justify-center min-w-0">
          <div className="relative w-full max-w-[1100px] aspect-[16/9] shadow-md ring-1 ring-[#E8E6E1] bg-white">
            <Slide
              slide={current}
              image={slideImageFor(current)}
              projectCover={projectCover}
              idx={active}
              total={deck.slides.length}
              lang={lang}
            />
          </div>
          <div className="flex items-center gap-4 mt-6">
            <button
              onClick={() => setActive((i) => Math.max(0, i - 1))}
              disabled={active === 0}
              className="inline-flex items-center justify-center w-10 h-10 border border-[#E8E6E1] rounded-sm hover:border-[#2563EB] disabled:opacity-30"
              data-testid="prev-slide-btn"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <div
              className="text-xs text-[#5C5B5A] tracking-[0.2em] uppercase"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
              data-testid="slide-counter"
            >
              {String(active + 1).padStart(2, "0")} / {String(deck.slides.length).padStart(2, "0")}
            </div>
            <button
              onClick={() => setActive((i) => Math.min(i + 1, deck.slides.length - 1))}
              disabled={active === deck.slides.length - 1}
              className="inline-flex items-center justify-center w-10 h-10 border border-[#E8E6E1] rounded-sm hover:border-[#2563EB] disabled:opacity-30"
              data-testid="next-slide-btn"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </section>

        {/* Edit panel */}
        <aside className="border-l border-[#E8E6E1] bg-[#FBFAF7] overflow-y-auto">
          <div className="p-4 lg:p-5">
            <EditPanel slide={current} onUpdate={updateActive} lang={lang} />
            <div className="mt-6 pt-6 border-t border-[#E8E6E1]">
              <CapTableSimulator
                defaultPreMoney={deck.slides.find((s) => s.key === "valuation")?.pre_money_mid || null}
                lang={lang}
              />
            </div>
          </div>
        </aside>
      </main>

      {/* Off-screen render of ALL slides at native 1280x720 for PDF export */}
      <div className="fixed -left-[10000px] top-0 pointer-events-none" aria-hidden="true">
        {deck.slides.map((s, i) => (
          <div
            key={i}
            ref={(el) => (slideRefs.current[i] = el)}
            className="relative bg-white"
            style={{ width: SLIDE_W, height: SLIDE_H }}
          >
            <Slide
              slide={s}
              image={slideImageFor(s)}
              projectCover={projectCover}
              idx={i}
              total={deck.slides.length}
              lang={lang}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
